// Audience Exclusions — Audiences section, LLM judgment row.
// Renders Claude's verdict on whether the account's audience-exclusion strategy is coherent
// with its business model. Supporting data: per-campaign exclusion count + breakdown.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface ExclusionRow {
  campaignId?: string;
  campaignName?: string;
  biddingStrategy?: string;
  exclusionCount: number;
  exclusionTypes: { USER_LIST: number; CUSTOM_AUDIENCE: number };
}

interface ExclusionsShape {
  totalSearchCampaigns: number;
  campaignsWithExclusions: number;
  campaignsWithoutExclusions: number;
  totalExclusions: number;
  perCampaign: ExclusionRow[];
}

const STRATEGY_LABEL: Record<string, string> = {
  MANUAL_CPC: 'Manual CPC',
  ENHANCED_CPC: 'Enhanced CPC',
  MAXIMIZE_CONVERSIONS: 'Max Conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Max Conv. Value',
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  TARGET_IMPRESSION_SHARE: 'Target Imp. Share',
  TARGET_SPEND: 'Target Spend',
  PERCENT_CPC: 'Percent CPC'
};

function strategyLabel(s: string | undefined): string {
  if (!s) return '—';
  return STRATEGY_LABEL[s] ?? s;
}

export const AudienceExclusionsEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as ExclusionsShape;

  // Campaigns without exclusions are the more actionable group for prospecting accounts —
  // surface those first.
  const sorted = [...e.perCampaign].sort((a, b) => {
    if ((a.exclusionCount === 0) !== (b.exclusionCount === 0))
      return a.exclusionCount === 0 ? -1 : 1;
    return b.exclusionCount - a.exclusionCount;
  });

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Exclusion coverage
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalSearchCampaigns} Search campaigns
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.campaignsWithExclusions.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>Campaigns with exclusions</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.campaignsWithoutExclusions.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>Campaigns with NO exclusions</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.totalExclusions.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Total exclusion attachments</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Per-campaign exclusion breakdown
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalSearchCampaigns} campaigns
          </span>
        </h4>
        <table className='evidence__table evidence__table--audience-exclusions'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Bidding strategy</th>
              <th className='evidence__th--right'>Exclusions</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(c => {
              const missing = c.exclusionCount === 0;
              return (
                <tr
                  key={c.campaignId ?? c.campaignName}
                  className={classNames({ 'evidence__row--flagged': missing })}
                >
                  <td>{c.campaignName ?? '(unnamed)'}</td>
                  <td>{strategyLabel(c.biddingStrategy)}</td>
                  <td className='evidence__td--right'>{c.exclusionCount.toLocaleString()}</td>
                  <td>
                    {missing ? (
                      <span className='evidence__inline-note'>No exclusions</span>
                    ) : (
                      <>
                        {c.exclusionTypes.USER_LIST > 0 && (
                          <span className='evidence__audience-chip'>
                            {c.exclusionTypes.USER_LIST} user-list
                          </span>
                        )}
                        {c.exclusionTypes.CUSTOM_AUDIENCE > 0 && (
                          <span className='evidence__audience-chip'>
                            {c.exclusionTypes.CUSTOM_AUDIENCE} custom
                          </span>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Exclusions = audience criteria marked <code>negative=true</code>. Common pattern:
        acquisition campaigns exclude past-converter / existing-customer user lists to avoid paying
        twice for users you've already won; brand campaigns typically don't need exclusions;
        retention campaigns include rather than exclude past purchasers.
      </p>
    </div>
  );
};
